import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  LENDER_IDs,
  getLoanApplicationDetailsUrl
} from "../../../shared/utils/Global";
import { LoanApplicationService } from "../../../services/LoanApplication";
import GetAuthContext from "../../../../../src/shared/context/AuthContext";

interface SmartLoanLinkProps {
  loanApplicationID: string;
  loanApplicationNum: string;
  sourceEntityID?: string;
  targetBlank?: boolean;
  userState: {
    userData?: {
      lenderID?: string;
    };
    organizationID?: string;
  };
  token: string;
  isLenderDashboard: boolean;
}

// Use local MFL_LOAN_TABS constant - matched exactly with API response keys
const MFL_LOAN_TABS = [
  { name: "MY_CHECKLIST" },
  { name: "DETAILS" },
  { name: "CO_LENDING" },
  { name: "APPLICATION_FORM" },
  { name: "COLLATERAL_DETAILS" },
  { name: "DISBURSEMENT" },
  { name: "BUSINESS_DETAILS" },
  { name: "VERIFICATIONS" },
  { name: "CO_APPLICANTS" },
  { name: "ALL_DOCUMENTS" },
  { name: "KYC" },
  { name: "UNDERWRITING" },
  { name: "BANK_INFO" },
  { name: "LOAN_OFFER" },
  { name: "INSURANCE" },
  { name: "E-REGISTRATIONS" },
  { name: "MANUAL_CREDIT_REVIEW" },
  { name: "ACTIVITY_HISTORY" }
] as const;

// Tab route mapping - extracted for better maintainability (matches API keys)
const TAB_ROUTE_MAP = {
  MY_CHECKLIST: "task-management",
  CO_LENDING: "co-lending",
  DETAILS: "",
  APPLICATION_FORM: "application-form",
  COLLATERAL_DETAILS: "collateral-details",
  DISBURSEMENT: "disbursement",
  BUSINESS_DETAILS: "business-details",
  VERIFICATIONS: "verifications",
  CO_APPLICANTS: "co-applicants",
  ALL_DOCUMENTS: "all-documents",
  KYC: "kyc",
  UNDERWRITING: "underwriting",
  BANK_INFO: "bank-info",
  LOAN_OFFER: "loan-offer",
  INSURANCE: "insurance",
  "E-REGISTRATIONS": "e-registrations/penny-drop",
  MANUAL_CREDIT_REVIEW: "credit-review/manual-credit-review",
  ACTIVITY_HISTORY: "timeline"
} as const;

/**
 * Hook for smart navigation logic that can be used programmatically
 */
export const useSmartLoanNavigation = () => {
  const navigate = useNavigate();
  const { userState, fetchToken } = GetAuthContext();
  const lenderID = userState?.userData?.lenderID;
  const token = fetchToken();

  // Memoize the navigation function to prevent unnecessary re-renders
  const navigateToPath = useCallback(
    (
      path: string,
      targetBlank: boolean = false,
      replaceCurrentPage: boolean = false
    ) => {
      if (targetBlank) {
        window.open(path, "_blank");
      } else if (replaceCurrentPage) {
        // Replace current URL (like a reload)
        window.location.replace(path);
      } else {
        navigate(path);
      }
    },
    [navigate]
  );

  const navigateToLoanApplication = useCallback(
    async (
      loanApplicationID: string,
      sourceEntityID: string,
      isLenderDashboard: boolean = false,
      targetBlank: boolean = false,
      replaceCurrentPage: boolean = false
    ) => {
      try {
        const organizationID = userState?.organizationID ?? "";

        // For non-MFL_LAP lenders, go directly to details page
        if (lenderID !== LENDER_IDs.MFL_LAP) {
          const targetPath = getLoanApplicationDetailsUrl(
            loanApplicationID,
            isLenderDashboard,
            sourceEntityID,
            organizationID,
            lenderID
          );

          navigateToPath(targetPath, targetBlank, replaceCurrentPage);
          return;
        }

        const defaultTargetPath = getLoanApplicationDetailsUrl(
          loanApplicationID,
          isLenderDashboard,
          sourceEntityID,
          organizationID,
          lenderID
        );

        try {
          const response = await LoanApplicationService.getPolicyUiConfig({
            loanID: loanApplicationID,
            token,
            isLenderDashboard,
            pageName: "Loan Application Details Page"
          });

          const responseData = response?.data as {
            tabs?: Record<string, { isVisible?: boolean }>;
          };

          if (response?.status && responseData?.tabs) {
            const availableTabs = responseData.tabs;

            const firstAvailableTab = MFL_LOAN_TABS.find(
              (priorityTab: { name: string }) => {
                const tabPermission = availableTabs[priorityTab.name];
                const isVisible = tabPermission?.isVisible === true;
                return isVisible;
              }
            );

            if (firstAvailableTab) {
              const routeSuffix =
                TAB_ROUTE_MAP[
                  firstAvailableTab.name as keyof typeof TAB_ROUTE_MAP
                ];
              console.log(
                `First available tab: ${firstAvailableTab.name}, Route: ${routeSuffix || "default details page"}`
              );

              const targetPath = routeSuffix
                ? `${defaultTargetPath}/${routeSuffix}`
                : defaultTargetPath;

              navigateToPath(targetPath, targetBlank, replaceCurrentPage);
            } else {
              navigateToPath(
                defaultTargetPath,
                targetBlank,
                replaceCurrentPage
              );
            }
          } else {
            navigateToPath(defaultTargetPath, targetBlank, replaceCurrentPage);
          }
        } catch (error) {
          console.error("Error fetching tab permissions:", error);
          navigateToPath(defaultTargetPath, targetBlank, replaceCurrentPage);
        }
      } catch (error) {
        console.error("Error in navigation:", error);
        // Fallback to default path
        const fallbackPath = getLoanApplicationDetailsUrl(
          loanApplicationID,
          isLenderDashboard,
          sourceEntityID,
          userState?.organizationID,
          lenderID
        );
        navigateToPath(fallbackPath, targetBlank, replaceCurrentPage);
      }
    },
    [lenderID, token, navigateToPath, userState]
  );

  return { navigateToLoanApplication };
};

/**
 * Smart component that handles dynamic routing based on available tabs
 * for MFL_LAP. Routes to the first available tab from permissions based on MFL_LOAN_TABS priority order.
 */
export default function SmartLoanLink({
  loanApplicationID,
  loanApplicationNum,
  sourceEntityID = "",
  targetBlank = false,
  isLenderDashboard
}: SmartLoanLinkProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { navigateToLoanApplication } = useSmartLoanNavigation();

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();

      if (isLoading) return; // Prevent multiple clicks

      setIsLoading(true);

      try {
        await navigateToLoanApplication(
          loanApplicationID,
          sourceEntityID,
          isLenderDashboard,
          targetBlank
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      targetBlank,
      loanApplicationID,
      sourceEntityID,
      isLenderDashboard,
      navigateToLoanApplication
    ]
  );

  return (
    <span
      className={`underline underline-offset-2 text-indigo-700 block cursor-pointer transition-colors duration-200 ${
        isLoading ? "cursor-wait opacity-75" : "hover:text-indigo-800"
      }`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !isLoading) {
          e.preventDefault();

          handleClick(e as unknown as React.MouseEvent);
        }
      }}
      title={isLoading ? "Loading..." : `Navigate to ${loanApplicationNum}`}
    >
      {loanApplicationNum}
    </span>
  );
}
